import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { GET_CUSTOMER_DATA, GET_CUSTOMER_ORDERS, UPDATE_CUSTOMER_PROFILE, CHANGE_CUSTOMER_PASSWORD, CREATE_CUSTOMER_ADDRESS, UPDATE_CUSTOMER_ADDRESS, DELETE_CUSTOMER_ADDRESS, GET_CUSTOMER_WISHLIST, REMOVE_PRODUCTS_FROM_WISHLIST, ADD_SIMPLE_PRODUCTS_TO_CART_FROM_WISHLIST, ADD_CONFIGURABLE_PRODUCTS_TO_CART_FROM_WISHLIST, UPDATE_WISHLIST_ITEMS, REORDER_ITEMS } from '../queries/customerQueries';
import { useNavigate } from 'react-router-dom';
import Loader from './Loader';
import './MyAccount.css';

const MyAccount = () => {
  const { user, logout, isInitializing } = useAuth();
  const { cartId, addToCart } = useCart();
  const [activeTab, setActiveTab] = useState('profile');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isEmailEditable, setIsEmailEditable] = useState(false);
  const [editFormData, setEditFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    date_of_birth: ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Address management state
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [addressEditMode, setAddressEditMode] = useState('add'); // 'add' or 'edit'
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressFormData, setAddressFormData] = useState({
    firstname: '',
    lastname: '',
    street: ['', ''],
    city: '',
    region_id: '',
    postcode: '',
    country_code: 'US',
    telephone: '',
    default_shipping: false,
    default_billing: false
  });
  const [addressError, setAddressError] = useState('');
  const [addressSuccess, setAddressSuccess] = useState('');

  // Wishlist management state
  const [wishlistActionError, setWishlistActionError] = useState('');
  const [wishlistActionSuccess, setWishlistActionSuccess] = useState('');
  const [wishlistPage, setWishlistPage] = useState(1);
  const [wishlistPageSize] = useState(6);

  // Reorder management state
  const [reorderLoading, setReorderLoading] = useState('');
  const [reorderError, setReorderError] = useState('');
  const [reorderSuccess, setReorderSuccess] = useState('');
  const navigate = useNavigate();

  const { data: customerData, loading: customerLoading, error: customerError, refetch: refetchCustomerData } = useQuery(GET_CUSTOMER_DATA);
  const { data: ordersData, loading: ordersLoading, error: ordersError } = useQuery(GET_CUSTOMER_ORDERS, {
    variables: { pageSize: 10, currentPage: 1 }
  });
  const { data: wishlistData, loading: wishlistLoading, error: wishlistError, refetch: refetchWishlist } = useQuery(GET_CUSTOMER_WISHLIST, {
    variables: { currentPage: wishlistPage, pageSize: wishlistPageSize }
  });

  const [updateCustomerProfile] = useMutation(UPDATE_CUSTOMER_PROFILE);
  const [changeCustomerPassword] = useMutation(CHANGE_CUSTOMER_PASSWORD);
  const [createCustomerAddress] = useMutation(CREATE_CUSTOMER_ADDRESS);
  const [updateCustomerAddress] = useMutation(UPDATE_CUSTOMER_ADDRESS);
  const [deleteCustomerAddress] = useMutation(DELETE_CUSTOMER_ADDRESS);
  const [removeProductsFromWishlist] = useMutation(REMOVE_PRODUCTS_FROM_WISHLIST);
  const [addSimpleProductsToCartFromWishlist] = useMutation(ADD_SIMPLE_PRODUCTS_TO_CART_FROM_WISHLIST);
  const [addConfigurableProductsToCartFromWishlist] = useMutation(ADD_CONFIGURABLE_PRODUCTS_TO_CART_FROM_WISHLIST);
  const [updateWishlistItems] = useMutation(UPDATE_WISHLIST_ITEMS);
  const [reorderItems] = useMutation(REORDER_ITEMS);
  
  useEffect(() => {
    // Redirect to customer account page by default
    navigate('/customer/account', { replace: true });
  }, [navigate]);

  if (isInitializing) {
    return (
      <div className="my-account-container">
        <Loader fullScreen={true} size="large" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="my-account-container">
        <div className="my-account-error">
          <h2>Access Denied</h2>
          <p>Please log in to access your account.</p>
        </div>
      </div>
    );
  }

  // Debug: Show token status
  const token = localStorage.getItem('customerToken');
  console.log('Token exists:', !!token);
  console.log('User data:', user);

  const tabs = [
    { id: 'profile', label: 'Profile Information' },
    { id: 'addresses', label: 'Address Book' },
    { id: 'orders', label: 'Order History' },
    { id: 'wishlist', label: 'My Wishlist' }
  ];

  const handleEditProfile = () => {
    setEditFormData({
      firstname: customerData?.customer?.firstname || '',
      lastname: customerData?.customer?.lastname || '',
      email: customerData?.customer?.email || user.email,
      date_of_birth: customerData?.customer?.date_of_birth || ''
    });
    setIsEditProfileModalOpen(true);
    setSaveError('');
    setSaveSuccess('');
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setIsEditProfileModalOpen(false);
    setIsEmailEditable(false);
    setEditFormData({
      firstname: '',
      lastname: '',
      email: '',
      date_of_birth: ''
    });
    setSaveError('');
    setSaveSuccess('');
  };

  const handleSaveProfile = async () => {
    try {
      setSaveError('');
      setSaveSuccess('');

      const updateInput = {
        firstname: editFormData.firstname,
        lastname: editFormData.lastname,
        date_of_birth: editFormData.date_of_birth || null
      };

      if (isEmailEditable) {
        updateInput.email = editFormData.email;
      }

      await updateCustomerProfile({
        variables: { input: updateInput }
      });

      await refetchCustomerData();
      setIsEditMode(false);
      setIsEditProfileModalOpen(false);
      setIsEmailEditable(false);
      setSaveSuccess('Profile updated successfully!');

      setTimeout(() => setSaveSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setSaveError(error.message || 'Failed to update profile. Please try again.');
    }
  };

  const handleInputChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOpenChangePassword = () => {
    setIsChangePasswordModalOpen(true);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleCloseChangePassword = () => {
    setIsChangePasswordModalOpen(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePassword = async () => {
    try {
      setPasswordError('');
      setPasswordSuccess('');

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setPasswordError('New passwords do not match.');
        return;
      }

      if (passwordData.newPassword.length < 8) {
        setPasswordError('New password must be at least 8 characters long.');
        return;
      }

      await changeCustomerPassword({
        variables: {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        }
      });

      setPasswordSuccess('Password changed successfully!');
      setTimeout(() => {
        setPasswordSuccess('');
        handleCloseChangePassword();
      }, 2000);
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError(error.message || 'Failed to change password. Please check your current password and try again.');
    }
  };

  // Address management functions
  const handleOpenAddAddress = () => {
    setAddressEditMode('add');
    setEditingAddress(null);
    setAddressFormData({
      firstname: '',
      lastname: '',
      street: ['', ''],
      city: '',
      region_id: '',
      postcode: '',
      country_code: 'US',
      telephone: '',
      default_shipping: false,
      default_billing: false
    });
    setIsAddressModalOpen(true);
    setAddressError('');
    setAddressSuccess('');
  };

  const handleOpenEditAddress = (address) => {
    setAddressEditMode('edit');
    setEditingAddress(address);
    setAddressFormData({
      firstname: address.firstname || '',
      lastname: address.lastname || '',
      street: address.street || ['', ''],
      city: address.city || '',
      region_id: address.region?.region_id || '',
      postcode: address.postcode || '',
      country_code: address.country_code || 'US',
      telephone: address.telephone || '',
      default_shipping: address.default_shipping || false,
      default_billing: address.default_billing || false
    });
    setIsAddressModalOpen(true);
    setAddressError('');
    setAddressSuccess('');
  };

  const handleCloseAddressModal = () => {
    setIsAddressModalOpen(false);
    setAddressEditMode('add');
    setEditingAddress(null);
    setAddressFormData({
      firstname: '',
      lastname: '',
      street: ['', ''],
      city: '',
      region_id: '',
      postcode: '',
      country_code: 'US',
      telephone: '',
      default_shipping: false,
      default_billing: false
    });
    setAddressError('');
    setAddressSuccess('');
  };

  const handleAddressInputChange = (field, value) => {
    if (field === 'street') {
      setAddressFormData(prev => ({
        ...prev,
        street: value
      }));
    } else {
      setAddressFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSaveAddress = async () => {
    try {
      setAddressError('');
      setAddressSuccess('');

      // Basic validation
      if (!addressFormData.firstname.trim() || !addressFormData.lastname.trim() ||
          !addressFormData.street[0].trim() || !addressFormData.city.trim() ||
          !addressFormData.postcode.trim()) {
        setAddressError('Please fill in all required fields (First Name, Last Name, Street, City, and Postal Code).');
        return;
      }

      // Check if region_id is required for the selected country
      if (addressFormData.country_code === 'US' && !addressFormData.region_id) {
        setAddressError('Please select a state for US addresses.');
        return;
      }

      const addressInput = {
        firstname: addressFormData.firstname.trim(),
        lastname: addressFormData.lastname.trim(),
        street: addressFormData.street.filter(s => s.trim()).map(s => s.trim()),
        city: addressFormData.city.trim(),
        postcode: addressFormData.postcode.trim(),
        country_code: addressFormData.country_code,
        telephone: addressFormData.telephone.trim() || null,
        default_shipping: addressFormData.default_shipping,
        default_billing: addressFormData.default_billing
      };

      // Add region object if region_id is provided - this is required for US addresses
      if (addressFormData.region_id) {
        addressInput.region = {
          region_id: parseInt(addressFormData.region_id)
        };
      }

      if (addressEditMode === 'edit' && editingAddress) {
        await updateCustomerAddress({
          variables: {
            id: parseInt(editingAddress.id),
            input: addressInput
          }
        });
        setAddressSuccess('Address updated successfully!');
      } else {
        await createCustomerAddress({
          variables: { input: addressInput }
        });
        setAddressSuccess('Address created successfully!');
      }

      await refetchCustomerData();

      setTimeout(() => {
        setAddressSuccess('');
        handleCloseAddressModal();
      }, 2000);
    } catch (error) {
      console.error('Error saving address:', error);
      setAddressError(error.message || 'Failed to save address. Please try again.');
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) {
      return;
    }

    try {
      await deleteCustomerAddress({
        variables: { id: parseInt(addressId) }
      });

      await refetchCustomerData();
      setAddressSuccess('Address deleted successfully!');
      setTimeout(() => setAddressSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting address:', error);
      setAddressError(error.message || 'Failed to delete address. Please try again.');
      setTimeout(() => setAddressError(''), 5000);
    }
  };

  // Wishlist management functions
  const handleRemoveFromWishlist = async (wishlistId, wishlistItemId) => {
    try {
      setWishlistActionError('');
      setWishlistActionSuccess('');

      await removeProductsFromWishlist({
        variables: {
          wishlistId: wishlistId,
          wishlistItemsIds: [wishlistItemId]
        }
      });

      await refetchWishlist();
      setWishlistActionSuccess('Item removed from wishlist!');
      setTimeout(() => setWishlistActionSuccess(''), 3000);
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      setWishlistActionError(error.message || 'Failed to remove item from wishlist.');
      setTimeout(() => setWishlistActionError(''), 5000);
    }
  };

  const handleMoveToCart = async (wishlistId, wishlistItem) => {
    try {
      setWishlistActionError('');
      setWishlistActionSuccess('');

      if (!cartId) {
        throw new Error('Cart not initialized');
      }

      // Add to cart using the cart context
      await addToCart(wishlistItem.product.sku, wishlistItem.quantity);

      // Remove from wishlist after adding to cart
      await removeProductsFromWishlist({
        variables: {
          wishlistId: wishlistId,
          wishlistItemsIds: [wishlistItem.id]
        }
      });

      await refetchWishlist();
      setWishlistActionSuccess('Item moved to cart!');
      setTimeout(() => setWishlistActionSuccess(''), 3000);
    } catch (error) {
      console.error('Error moving to cart:', error);
      setWishlistActionError(error.message || 'Failed to move item to cart.');
      setTimeout(() => setWishlistActionError(''), 5000);
    }
  };

  const handleUpdateWishlistQuantity = async (wishlistId, wishlistItemId, newQuantity) => {
    if (newQuantity < 1) return;

    try {
      setWishlistActionError('');
      setWishlistActionSuccess('');

      await updateWishlistItems({
        variables: {
          wishlistId: wishlistId,
          wishlistItems: [{
            wishlist_item_id: wishlistItemId,
            quantity: newQuantity
          }]
        }
      });

      await refetchWishlist();
      setWishlistActionSuccess('Quantity updated!');
      setTimeout(() => setWishlistActionSuccess(''), 2000);
    } catch (error) {
      console.error('Error updating quantity:', error);
      setWishlistActionError(error.message || 'Failed to update quantity.');
      setTimeout(() => setWishlistActionError(''), 5000);
    }
  };

  const handleReorder = async (orderNumber) => {
    try {
      setReorderLoading(orderNumber);
      setReorderError('');
      setReorderSuccess('');

      const result = await reorderItems({
        variables: { orderNumber: orderNumber }
      });

      const reorderResult = result.data?.reorderItems;

      if (reorderResult?.userInputErrors && reorderResult.userInputErrors.length > 0) {
        const errorMessages = reorderResult.userInputErrors.map(error => error.message).join(', ');
        throw new Error(errorMessages);
      }

      if (reorderResult?.cart?.id) {
        setReorderSuccess('Items successfully added to your cart!');
        setTimeout(() => setReorderSuccess(''), 5000);
      } else {
        throw new Error('Failed to add items to cart');
      }
    } catch (error) {
      console.error('Error reordering:', error);
      setReorderError(error.message || 'Failed to reorder items. Some items may no longer be available.');
      setTimeout(() => setReorderError(''), 8000);
    } finally {
      setReorderLoading('');
    }
  };

  const renderProfile = () => (
    <div className="account-section">
      <h3>Profile Information</h3>
      {customerLoading ? (
        <div className="loading">Loading profile...</div>
      ) : customerError ? (
        <div className="error">
          Error loading profile: {customerError.message}
          {customerError.graphQLErrors && customerError.graphQLErrors.length > 0 && (
            <div>
              <p>GraphQL Errors:</p>
              {customerError.graphQLErrors.map((err, index) => (
                <pre key={index}>{JSON.stringify(err, null, 2)}</pre>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="profile-info">
          {saveSuccess && <div className="success-message">{saveSuccess}</div>}
          {saveError && <div className="error-message">{saveError}</div>}

          <div className="profile-main">
            <div className="profile-display">
              <p className="profile-name">
                {customerData?.customer?.firstname || ''} {customerData?.customer?.lastname || ''}
              </p>
              <p className="profile-email">{customerData?.customer?.email || user.email}</p>
              {customerData?.customer?.date_of_birth && (
                <p className="profile-dob">Born: {new Date(customerData.customer.date_of_birth).toLocaleDateString()}</p>
              )}
            </div>
            <div className="profile-actions">
              <button className="btn-primary" onClick={handleEditProfile}>Edit Profile</button>
              <button className="btn-secondary" onClick={handleOpenChangePassword}>Change Password</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderAddresses = () => (
    <div className="account-section">
      <h3>Address Book</h3>
      {addressSuccess && <div className="success-message">{addressSuccess}</div>}
      {addressError && <div className="error-message">{addressError}</div>}
      <div className="addresses-grid">
        {customerData?.customer?.addresses?.length > 0 ? (
          customerData.customer.addresses.map((address) => (
            <div key={address.id} className="address-card">
              <div className="address-header">
                <h4>{address.firstname} {address.lastname}</h4>
              </div>
              <div className="address-details">
                <p>{address.street?.join(', ')}</p>
                <p>{address.city}, {address.region?.region || ''} {address.postcode}</p>
                <p>{address.country_code}</p>
                {address.telephone && <p>Phone: {address.telephone}</p>}
                {address.company && <p>Company: {address.company}</p>}
              </div>
              <div className="address-badges">
                {address.default_shipping && <span className="badge">Default Shipping</span>}
                {address.default_billing && <span className="badge">Default Billing</span>}
              </div>
              <div className="address-actions">
                <button className="btn-secondary" onClick={() => handleOpenEditAddress(address)}>Edit</button>
                <button className="btn-danger" onClick={() => handleDeleteAddress(address.id)}>Delete</button>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <p>No addresses found</p>
            <button className="btn-primary" onClick={handleOpenAddAddress}>Add New Address</button>
          </div>
        )}
      </div>
      <button className="btn-primary" onClick={handleOpenAddAddress}>Add New Address</button>
    </div>
  );

  const renderOrders = () => (
    <div className="account-section">
      <h3>Order History</h3>
      {reorderSuccess && <div className="success-message">{reorderSuccess}</div>}
      {reorderError && <div className="error-message">{reorderError}</div>}
      {ordersLoading ? (
        <div className="loading">Loading orders...</div>
      ) : ordersError ? (
        <div className="error">Error loading orders: {ordersError.message}</div>
      ) : (
        <div className="orders-list">
          {ordersData?.customer?.orders?.items?.length > 0 ? (
            ordersData.customer.orders.items.map((order) => (
              <div key={order.id} className="order-card">
                <div className="order-header">
                  <div className="order-info">
                    <h4>Order #{order.number}</h4>
                    <p className="order-date">
                      Placed on {new Date(order.order_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                  <div className="order-status">
                    <span className={`status ${order.status.toLowerCase()}`}>
                      {order.status}
                    </span>
                  </div>
                </div>
                <div className="order-details">
                  <div className="order-total">
                    <strong>
                      {order.total?.grand_total?.value} {order.total?.grand_total?.currency}
                    </strong>
                  </div>
                  <button
                    className="btn-secondary"
                    onClick={() => openOrderDetails(order)}
                  >
                    View Details
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => handleReorder(order.number)}
                    disabled={reorderLoading === order.number}
                  >
                    {reorderLoading === order.number ? 'Reordering...' : 'Reorder'}
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <p>No orders found</p>
              <button className="btn-primary">Start Shopping</button>
            </div>
          )}
        </div>
      )}
    </div>
  );


  const renderWishlist = () => {
    // Get the first wishlist (customers typically have one default wishlist)
    const wishlist = wishlistData?.customer?.wishlists?.[0];
    const wishlistItems = wishlist?.items_v2?.items || [];
    const pageInfo = wishlist?.items_v2?.page_info;

    return (
      <div className="account-section">
        <h3>My Wishlist</h3>
        {wishlistActionSuccess && <div className="success-message">{wishlistActionSuccess}</div>}
        {wishlistActionError && <div className="error-message">{wishlistActionError}</div>}

        {wishlistLoading ? (
          <div className="loading">Loading wishlist...</div>
        ) : wishlistError ? (
          <div className="error">Error loading wishlist: {wishlistError.message}</div>
        ) : wishlistItems.length > 0 ? (
          <div className="wishlist-grid">
            {wishlistItems.map((item) => (
              <div key={item.id} className="wishlist-item-card">
                <Link to={`/${item.product.url_key}.html`} style={{ textDecoration: 'none' }}>
                  {item.product.image ? (
                    <img
                      src={item.product.image.url}
                      alt={item.product.image.label || item.product.name}
                      className="wishlist-item-image"
                      loading="lazy"
                      style={{ cursor: 'pointer' }}
                    />
                  ) : (
                    <div className="wishlist-item-image no-image-placeholder" style={{ cursor: 'pointer' }}>No Image</div>
                  )}
                </Link>
                <div className="wishlist-item-header">
                  <div className="wishlist-item-info">
                    <Link to={`/${item.product.url_key}.html`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <h4 className="wishlist-item-name" style={{ cursor: 'pointer' }}>{item.product.name}</h4>
                    </Link>
                    <div className="wishlist-item-price">
                      {item.product.price_range?.minimum_price?.final_price && (
                        <span className="price">
                          {item.product.price_range.minimum_price.final_price.value} {item.product.price_range.minimum_price.final_price.currency}
                        </span>
                      )}
                      {item.product.price_range?.minimum_price?.regular_price &&
                       item.product.price_range.minimum_price.regular_price.value !== item.product.price_range.minimum_price.final_price.value && (
                        <span className="regular-price">
                          {item.product.price_range.minimum_price.regular_price.value} {item.product.price_range.minimum_price.regular_price.currency}
                        </span>
                      )}
                    </div>
                    <div className="wishlist-item-quantity" style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <label htmlFor={`qty-${item.id}`} style={{ fontSize: '0.875rem', fontWeight: '500', color: '#6b7280' }}>
                        Quantity:
                      </label>
                      <input
                        type="number"
                        id={`qty-${item.id}`}
                        min="1"
                        max="99"
                        value={item.quantity}
                        onChange={(e) => handleUpdateWishlistQuantity(wishlist.id, item.id, parseInt(e.target.value) || 1)}
                        style={{
                          width: '70px',
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          textAlign: 'center',
                          outline: 'none',
                          transition: 'border-color 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                        onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                      />
                    </div>
                    {item.added_at && (
                      <p className="wishlist-item-date">
                        Added: {new Date(item.added_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
                <div className="wishlist-item-actions">
                  <button
                    className="btn-primary"
                    onClick={() => handleMoveToCart(wishlist.id, item)}
                  >
                    Move to Cart
                  </button>
                  <button
                    className="btn-danger"
                    onClick={() => handleRemoveFromWishlist(wishlist.id, item.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>Your wishlist is empty</p>
            <button className="btn-primary" onClick={() => window.location.href = '/'}>
              Continue Shopping
            </button>
          </div>
        )}

        {/* Wishlist Pagination */}
        {pageInfo && pageInfo.total_pages > 1 && (
          <div className="pagination" style={{ marginTop: '2rem' }}>
            <button
              className="pagination-btn"
              onClick={() => setWishlistPage(wishlistPage - 1)}
              disabled={wishlistPage <= 1}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: wishlistPage <= 1 ? '#ccc' : '#7c3aed',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: wishlistPage <= 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Previous
            </button>
            <span className="pagination-info" style={{ margin: '0 1rem', fontWeight: '500' }}>
              Page {pageInfo.current_page} of {pageInfo.total_pages}
            </span>
            <button
              className="pagination-btn"
              onClick={() => setWishlistPage(wishlistPage + 1)}
              disabled={wishlistPage >= pageInfo.total_pages}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: wishlistPage >= pageInfo.total_pages ? '#ccc' : '#7c3aed',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: wishlistPage >= pageInfo.total_pages ? 'not-allowed' : 'pointer'
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  };


  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setIsOrderModalOpen(true);
  };

  const closeOrderModal = () => {
    setIsOrderModalOpen(false);
    setSelectedOrder(null);
  };

  const renderOrderDetailsModal = () => {
    if (!isOrderModalOpen || !selectedOrder) return null;

    return (
      <div className="modal-overlay" onClick={closeOrderModal}>
        <div className="order-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Order Details - #{selectedOrder.number}</h2>
            <button className="modal-close" onClick={closeOrderModal}>×</button>
          </div>

          <div className="modal-content">
            <div className="order-summary">
              <div className="order-info-grid">
                <div className="info-item">
                  <label>Order Date</label>
                  <span>{new Date(selectedOrder.order_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}</span>
                </div>
                <div className="info-item">
                  <label>Status</label>
                  <span className={`status ${selectedOrder.status.toLowerCase()}`}>
                    {selectedOrder.status}
                  </span>
                </div>
                <div className="info-item">
                  <label>Total</label>
                  <span className="total-amount">
                    {selectedOrder.total?.grand_total?.value} {selectedOrder.total?.grand_total?.currency}
                  </span>
                </div>
              </div>
            </div>

            {/* Order Items */}
            {selectedOrder.items && selectedOrder.items.length > 0 && (
              <div className="order-items-section">
                <h3>Items Ordered</h3>
                <div className="order-items">
                  {selectedOrder.items.map((item, index) => (
                    <div key={item.id || index} className="order-item">
                      <div className="item-details">
                        <h4>{item.product_name}</h4>
                        <p>SKU: {item.product_sku}</p>
                        <p>Quantity: {item.quantity_ordered}</p>
                      </div>
                      <div className="item-price">
                        {item.product_sale_price?.value} {item.product_sale_price?.currency}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Shipping Address */}
            {selectedOrder.shipping_address && (
              <div className="address-section">
                <h3>Shipping Address</h3>
                <div className="address-details">
                  <p><strong>{selectedOrder.shipping_address.firstname} {selectedOrder.shipping_address.lastname}</strong></p>
                  <p>{selectedOrder.shipping_address.street?.join(', ')}</p>
                  <p>{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.region} {selectedOrder.shipping_address.postcode}</p>
                  <p>{selectedOrder.shipping_address.country_code}</p>
                  {selectedOrder.shipping_address.telephone && <p>Phone: {selectedOrder.shipping_address.telephone}</p>}
                </div>
              </div>
            )}

            {/* Billing Address */}
            {selectedOrder.billing_address && (
              <div className="address-section">
                <h3>Billing Address</h3>
                <div className="address-details">
                  <p><strong>{selectedOrder.billing_address.firstname} {selectedOrder.billing_address.lastname}</strong></p>
                  <p>{selectedOrder.billing_address.street?.join(', ')}</p>
                  <p>{selectedOrder.billing_address.city}, {selectedOrder.billing_address.region} {selectedOrder.billing_address.postcode}</p>
                  <p>{selectedOrder.billing_address.country_code}</p>
                  {selectedOrder.billing_address.telephone && <p>Phone: {selectedOrder.billing_address.telephone}</p>}
                </div>
              </div>
            )}

            {/* Payment Method */}
            {selectedOrder.payment_methods && selectedOrder.payment_methods.length > 0 && (
              <div className="payment-section">
                <h3>Payment Method</h3>
                <div className="payment-details">
                  {selectedOrder.payment_methods.map((payment, index) => (
                    <p key={index}><strong>{payment.name}</strong> ({payment.type})</p>
                  ))}
                </div>
              </div>
            )}


            {/* Order Totals */}
            {selectedOrder.total && (
              <div className="totals-section">
                <h3>Order Totals</h3>
                <div className="totals-grid">
                  {selectedOrder.total.subtotal && (
                    <div className="total-row">
                      <span>Subtotal:</span>
                      <span>{selectedOrder.total.subtotal.value} {selectedOrder.total.subtotal.currency}</span>
                    </div>
                  )}
                  {selectedOrder.total.total_shipping && (
                    <div className="total-row">
                      <span>Shipping:</span>
                      <span>{selectedOrder.total.total_shipping.value} {selectedOrder.total.total_shipping.currency}</span>
                    </div>
                  )}
                  {selectedOrder.total.total_tax && (
                    <div className="total-row">
                      <span>Tax:</span>
                      <span>{selectedOrder.total.total_tax.value} {selectedOrder.total.total_tax.currency}</span>
                    </div>
                  )}
                  <div className="total-row grand-total">
                    <span>Grand Total:</span>
                    <span>{selectedOrder.total.grand_total.value} {selectedOrder.total.grand_total.currency}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEditProfileModal = () => {
    if (!isEditProfileModalOpen) return null;

    return (
      <div className="modal-overlay" onClick={handleCancelEdit}>
        <div className="edit-profile-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Edit Profile</h2>
            <button className="modal-close" onClick={handleCancelEdit}>×</button>
          </div>

          <div className="modal-content">
            {saveSuccess && <div className="success-message">{saveSuccess}</div>}
            {saveError && <div className="error-message">{saveError}</div>}

            <div className="profile-edit">
              <div className="profile-grid">
                <div className="form-group">
                  <label htmlFor="firstname">First Name</label>
                  <input
                    type="text"
                    id="firstname"
                    value={editFormData.firstname}
                    onChange={(e) => handleInputChange('firstname', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastname">Last Name</label>
                  <input
                    type="text"
                    id="lastname"
                    value={editFormData.lastname}
                    onChange={(e) => handleInputChange('lastname', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <div className="email-field-header">
                    <label htmlFor="email">Email Address</label>
                    <div className="email-checkbox">
                      <input
                        type="checkbox"
                        id="enableEmailEdit"
                        checked={isEmailEditable}
                        onChange={(e) => setIsEmailEditable(e.target.checked)}
                      />
                      <label htmlFor="enableEmailEdit" className="checkbox-label">
                        Allow email change
                      </label>
                    </div>
                  </div>
                  <input
                    type="email"
                    id="email"
                    value={editFormData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    readOnly={!isEmailEditable}
                    style={{
                      background: isEmailEditable ? 'white' : '#f5f5f5',
                      cursor: isEmailEditable ? 'text' : 'not-allowed'
                    }}
                    placeholder={isEmailEditable ? 'Enter your email address' : ''}
                  />
                  {isEmailEditable && (
                    <small className="email-warning">
                      ⚠️ Changing your email may require verification and could affect your login credentials.
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="dateOfBirth">Date of Birth</label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    value={editFormData.date_of_birth}
                    onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                  />
                </div>
              </div>
              <div className="profile-actions">
                <button className="btn-primary" onClick={handleSaveProfile}>Save Changes</button>
                <button className="btn-secondary" onClick={handleCancelEdit}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderChangePasswordModal = () => {
    if (!isChangePasswordModalOpen) return null;

    return (
      <div className="modal-overlay" onClick={handleCloseChangePassword}>
        <div className="change-password-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Change Password</h2>
            <button className="modal-close" onClick={handleCloseChangePassword}>×</button>
          </div>

          <div className="modal-content">
            {passwordSuccess && <div className="success-message">{passwordSuccess}</div>}
            {passwordError && <div className="error-message">{passwordError}</div>}

            <div className="password-form">
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                  placeholder="Enter your current password"
                />
              </div>
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                  placeholder="Enter new password (min 8 characters)"
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                  placeholder="Confirm new password"
                />
              </div>
              <div className="password-actions">
                <button
                  className="btn-primary"
                  onClick={handleChangePassword}
                  disabled={!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                >
                  Change Password
                </button>
                <button className="btn-secondary" onClick={handleCloseChangePassword}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAddressModal = () => {
    if (!isAddressModalOpen) return null;

    return (
      <div className="modal-overlay" onClick={handleCloseAddressModal}>
        <div className="address-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{addressEditMode === 'edit' ? 'Edit Address' : 'Add New Address'}</h2>
            <button className="modal-close" onClick={handleCloseAddressModal}>×</button>
          </div>

          <div className="modal-content">
            {addressSuccess && <div className="success-message">{addressSuccess}</div>}
            {addressError && <div className="error-message">{addressError}</div>}

            <div className="address-form">
              <div className="address-form-grid">
                <div className="form-group">
                  <label htmlFor="addressFirstname">First Name *</label>
                  <input
                    type="text"
                    id="addressFirstname"
                    value={addressFormData.firstname}
                    onChange={(e) => handleAddressInputChange('firstname', e.target.value)}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="addressLastname">Last Name *</label>
                  <input
                    type="text"
                    id="addressLastname"
                    value={addressFormData.lastname}
                    onChange={(e) => handleAddressInputChange('lastname', e.target.value)}
                    placeholder="Enter last name"
                    required
                  />
                </div>
                <div className="form-group form-group-full">
                  <label htmlFor="addressStreet1">Street Address *</label>
                  <input
                    type="text"
                    id="addressStreet1"
                    value={addressFormData.street[0]}
                    onChange={(e) => {
                      const newStreet = [...addressFormData.street];
                      newStreet[0] = e.target.value;
                      handleAddressInputChange('street', newStreet);
                    }}
                    placeholder="Enter street address"
                    required
                  />
                </div>
                <div className="form-group form-group-full">
                  <label htmlFor="addressStreet2">Apartment, suite, etc.</label>
                  <input
                    type="text"
                    id="addressStreet2"
                    value={addressFormData.street[1] || ''}
                    onChange={(e) => {
                      const newStreet = [...addressFormData.street];
                      newStreet[1] = e.target.value;
                      handleAddressInputChange('street', newStreet);
                    }}
                    placeholder="Apartment, suite, etc. (optional)"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="addressCity">City *</label>
                  <input
                    type="text"
                    id="addressCity"
                    value={addressFormData.city}
                    onChange={(e) => handleAddressInputChange('city', e.target.value)}
                    placeholder="Enter city"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="addressRegion">State/Region {addressFormData.country_code === 'US' ? '*' : ''}</label>
                  {addressFormData.country_code === 'US' ? (
                    <select
                      id="addressRegion"
                      value={addressFormData.region_id}
                      onChange={(e) => handleAddressInputChange('region_id', e.target.value)}
                      required={addressFormData.country_code === 'US'}
                    >
                      <option value="">Select State</option>
                      <option value="1">Alabama</option>
                      <option value="2">Alaska</option>
                      <option value="3">Arizona</option>
                      <option value="4">Arkansas</option>
                      <option value="5">California</option>
                      <option value="6">Colorado</option>
                      <option value="7">Connecticut</option>
                      <option value="8">Delaware</option>
                      <option value="9">Florida</option>
                      <option value="10">Georgia</option>
                      <option value="11">Hawaii</option>
                      <option value="12">Idaho</option>
                      <option value="13">Illinois</option>
                      <option value="14">Indiana</option>
                      <option value="15">Iowa</option>
                      <option value="16">Kansas</option>
                      <option value="17">Kentucky</option>
                      <option value="18">Louisiana</option>
                      <option value="19">Maine</option>
                      <option value="20">Maryland</option>
                      <option value="21">Massachusetts</option>
                      <option value="22">Michigan</option>
                      <option value="23">Minnesota</option>
                      <option value="24">Mississippi</option>
                      <option value="25">Missouri</option>
                      <option value="26">Montana</option>
                      <option value="27">Nebraska</option>
                      <option value="28">Nevada</option>
                      <option value="29">New Hampshire</option>
                      <option value="30">New Jersey</option>
                      <option value="31">New Mexico</option>
                      <option value="32">New York</option>
                      <option value="33">North Carolina</option>
                      <option value="34">North Dakota</option>
                      <option value="35">Ohio</option>
                      <option value="36">Oklahoma</option>
                      <option value="37">Oregon</option>
                      <option value="38">Pennsylvania</option>
                      <option value="39">Rhode Island</option>
                      <option value="40">South Carolina</option>
                      <option value="41">South Dakota</option>
                      <option value="42">Tennessee</option>
                      <option value="43">Texas</option>
                      <option value="44">Utah</option>
                      <option value="45">Vermont</option>
                      <option value="46">Virginia</option>
                      <option value="47">Washington</option>
                      <option value="48">West Virginia</option>
                      <option value="49">Wisconsin</option>
                      <option value="50">Wyoming</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      id="addressRegion"
                      value={addressFormData.region_id}
                      onChange={(e) => handleAddressInputChange('region_id', e.target.value)}
                      placeholder="Enter state or region"
                    />
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="addressPostcode">Postal Code *</label>
                  <input
                    type="text"
                    id="addressPostcode"
                    value={addressFormData.postcode}
                    onChange={(e) => handleAddressInputChange('postcode', e.target.value)}
                    placeholder="Enter postal code"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="addressCountry">Country</label>
                  <select
                    id="addressCountry"
                    value={addressFormData.country_code}
                    onChange={(e) => handleAddressInputChange('country_code', e.target.value)}
                  >
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="GB">United Kingdom</option>
                    <option value="DE">Germany</option>
                    <option value="FR">France</option>
                    <option value="AU">Australia</option>
                  </select>
                </div>
                <div className="form-group form-group-full">
                  <div className="checkbox-group">
                    <div className="checkbox-item">
                      <input
                        type="checkbox"
                        id="defaultShipping"
                        checked={addressFormData.default_shipping}
                        onChange={(e) => handleAddressInputChange('default_shipping', e.target.checked)}
                      />
                      <label htmlFor="defaultShipping" className="checkbox-label">Set as default shipping address</label>
                    </div>
                    <div className="checkbox-item">
                      <input
                        type="checkbox"
                        id="defaultBilling"
                        checked={addressFormData.default_billing}
                        onChange={(e) => handleAddressInputChange('default_billing', e.target.checked)}
                      />
                      <label htmlFor="defaultBilling" className="checkbox-label">Set as default billing address</label>
                    </div>
                  </div>
                </div>
                <div className="form-group form-group-full">
                  <label htmlFor="addressTelephone">Phone Number</label>
                  <input
                    type="tel"
                    id="addressTelephone"
                    value={addressFormData.telephone}
                    onChange={(e) => handleAddressInputChange('telephone', e.target.value)}
                    placeholder="Enter phone number (optional)"
                  />
                </div>
              </div>
              <div className="address-actions">
                <button
                  className="btn-primary"
                  onClick={handleSaveAddress}
                  disabled={!addressFormData.firstname.trim() || !addressFormData.lastname.trim() ||
                           !addressFormData.street[0].trim() || !addressFormData.city.trim() ||
                           !addressFormData.postcode.trim() ||
                           (addressFormData.country_code === 'US' && !addressFormData.region_id)}
                >
                  {addressEditMode === 'edit' ? 'Update Address' : 'Add Address'}
                </button>
                <button className="btn-secondary" onClick={handleCloseAddressModal}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return renderProfile();
      case 'addresses':
        return renderAddresses();
      case 'orders':
        return renderOrders();
      case 'wishlist':
        return renderWishlist();
      default:
        return renderProfile();
    }
  };

  return (
    <div className="my-account-container">
      <div className="account-header">
        <h1>My Account</h1>
        <p>Welcome back, {customerData?.customer?.firstname || user.email}!</p>
        <button className="logout-btn" onClick={logout}>
          Logout
        </button>
      </div>

      <div className="account-content">
        <nav className="account-nav">
          <ul className="nav-tabs">
            {tabs.map((tab) => (
              <li key={tab.id}>
                <button
                  className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="tab-label">{tab.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <main className="account-main">
          {renderTabContent()}
        </main>
      </div>

      {renderOrderDetailsModal()}
      {renderEditProfileModal()}
      {renderChangePasswordModal()}
      {renderAddressModal()}
    </div>
  );
};

export default MyAccount;